import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from './AuthContext';
import PaymentModal from '../PaymentModal';

const { FiUser, FiMail, FiLock, FiSave, FiAlertCircle, FiCheckCircle, FiArrowLeft, FiStar, FiToggleLeft, FiToggleRight } = FiIcons;

const ProfileForm = () => {
  const { 
    user, 
    authError, 
    updatePassword, 
    userMetadata, 
    updateUserMetadata, 
    isPremium, 
    toggleSubscriptionStatus, 
    showPaymentModal,
    setShowPaymentModal
  } = useAuth();
  
  const navigate = useNavigate();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTogglingSubscription, setIsTogglingSubscription] = useState(false);

  // Reset form states when component mounts or user changes
  useEffect(() => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setFormError('');
    setSuccessMessage('');
    setIsSubmitting(false);
    
    if (userMetadata?.display_name) {
      setDisplayName(userMetadata.display_name);
    }
  }, [user, userMetadata]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setFormError('');
    setSuccessMessage('');
    
    // Validate passwords
    if (!currentPassword || !newPassword || !confirmPassword) {
      setFormError('All password fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setFormError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setFormError('New password must be at least 6 characters long');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // In a real implementation, you would verify the current password first
      // This is a simplified version
      const { error, success } = await updatePassword(newPassword);
      
      if (error) {
        setFormError(error.message || 'Failed to update password');
      } else if (success) {
        setSuccessMessage('Password updated successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error) {
      setFormError('An unexpected error occurred');
      console.error('Update password error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleDisplayNameUpdate = async (e) => {
    e.preventDefault();
    setFormError('');
    setSuccessMessage('');
    
    setIsSubmitting(true);
    
    try {
      const { error } = await updateUserMetadata({ display_name: displayName });
      
      if (error) {
        setFormError(error.message || 'Failed to update profile');
      } else {
        setSuccessMessage('Profile updated successfully');
      }
    } catch (error) {
      setFormError('An unexpected error occurred');
      console.error('Update profile error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleToggleSubscription = async () => {
    setIsTogglingSubscription(true);
    try {
      const { error } = await toggleSubscriptionStatus();
      
      if (error) {
        setFormError(error.message || 'Failed to update subscription status');
      } else {
        setSuccessMessage(
          userMetadata?.subscription_active
            ? 'Subscription deactivated'
            : 'Subscription reactivated'
        );
      }
    } catch (error) {
      setFormError('An unexpected error occurred');
      console.error('Toggle subscription error:', error);
    } finally {
      setIsTogglingSubscription(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-lg p-8"
    >
      {/* Back button */}
      <button 
        onClick={() => navigate('/')}
        className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 mb-6"
      >
        <SafeIcon icon={FiArrowLeft} />
        <span>Back to Calculator</span>
      </button>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Your Profile</h2>
        <p className="text-gray-600">Manage your account information</p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <SafeIcon icon={FiCheckCircle} className="text-green-500" />
          <span>{successMessage}</span>
        </div>
      )}
      
      {(formError || authError) && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <SafeIcon icon={FiAlertCircle} className="text-red-500" />
          <span>{formError || authError}</span>
        </div>
      )}

      {/* User Information */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Account Information</h3>
        
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-indigo-100 rounded-full p-4">
              <SafeIcon icon={FiUser} className="text-2xl text-indigo-600" />
            </div>
            <div>
              <p className="font-medium text-lg">{userMetadata?.display_name || 'User'}</p>
              <p className="text-gray-500">{user.email}</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Account created:</span>
              <span className="text-gray-700 font-medium">{new Date(user.created_at).toLocaleDateString()}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Last sign in:</span>
              <span className="text-gray-700 font-medium">
                {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString() : 'Never'}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Account status:</span>
              {userMetadata?.premium_status ? (
                <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-3 py-1 rounded-full flex items-center gap-1">
                  <SafeIcon icon={FiStar} className="text-sm" />
                  <span className="font-medium">Premium</span>
                </div>
              ) : (
                <span className="text-gray-700 font-medium">Free</span>
              )}
            </div>
            
            {userMetadata?.premium_status && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Premium since:</span>
                  <span className="text-gray-700 font-medium">
                    {userMetadata?.premium_since ? new Date(userMetadata.premium_since).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Subscription status:</span>
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${userMetadata?.subscription_active ? 'text-green-600' : 'text-red-600'}`}>
                      {userMetadata?.subscription_active ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      onClick={handleToggleSubscription}
                      disabled={isTogglingSubscription}
                      className="text-indigo-600 hover:text-indigo-700 transition-colors disabled:opacity-50"
                    >
                      <SafeIcon 
                        icon={userMetadata?.subscription_active ? FiToggleRight : FiToggleLeft} 
                        className="text-2xl" 
                      />
                    </button>
                  </div>
                </div>
              </>
            )}
            
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Free calculations used:</span>
              <span className="text-gray-700 font-medium">
                {userMetadata?.free_trials_used || 0} of 3
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Calculations remaining:</span>
              {isPremium ? (
                <span className="text-green-600 font-medium">Unlimited</span>
              ) : (
                <span className="text-gray-700 font-medium">
                  {Math.max(0, 3 - (userMetadata?.free_trials_used || 0))}
                </span>
              )}
            </div>
          </div>
          
          {!userMetadata?.premium_status && (
            <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                <SafeIcon icon={FiStar} className="text-yellow-500" />
                Upgrade to Premium
              </h4>
              <p className="text-indigo-700 text-sm mb-4">
                Get unlimited meal calculations and access to all premium features.
              </p>
              <button 
                onClick={() => setShowPaymentModal(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Upgrade Now
              </button>
            </div>
          )}
          
          {userMetadata?.premium_status && !userMetadata?.subscription_active && (
            <div className="mt-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h4 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                <SafeIcon icon={FiStar} className="text-orange-500" />
                Reactivate Your Subscription
              </h4>
              <p className="text-orange-700 text-sm mb-4">
                Your premium subscription is currently inactive. Reactivate to continue enjoying unlimited access.
              </p>
              <button 
                onClick={() => setShowPaymentModal(true)}
                className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Reactivate Subscription
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Update Display Name Form */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Update Display Name</h3>
        
        <form onSubmit={handleDisplayNameUpdate} className="space-y-4">
          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-1">
              Display Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiUser} className="text-gray-400" />
              </div>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your display name"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          
          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-6 rounded-lg font-medium transition-colors flex items-center gap-2 disabled:bg-indigo-400"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} />
                  <span>Update Profile</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Change Password Form */}
      <div>
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Change Password</h3>
        
        <form onSubmit={handlePasswordChange} className="space-y-4">
          {/* Current Password */}
          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Current Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiLock} className="text-gray-400" />
              </div>
              <input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiLock} className="text-gray-400" />
              </div>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
                minLength={6}
              />
            </div>
          </div>

          {/* Confirm New Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiLock} className="text-gray-400" />
              </div>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-6 rounded-lg font-medium transition-colors flex items-center gap-2 disabled:bg-indigo-400"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} />
                  <span>Update Password</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default ProfileForm;